"use client";

import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@west-tennessee-consulting/ui";

import type {
  SupportInboxResponse,
  SupportInboxThreadResponse,
  SupportInboxThreadSummary,
  SupportMessageStatus,
} from "./types";

const SUPPORT_INBOX_KEY = "support-inbox";

/** Poll the live list every 30s (design doc §3.5 — SWR-style poll). */
const LIST_REFETCH_INTERVAL_MS = 30_000;

async function authedFetch<T>(url: string, token: string | null): Promise<T> {
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    cache: "no-store",
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      // non-JSON error body — keep the status message
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

/** Live support-inbox list for a company (polled). */
export function useSupportInbox(companyId: string) {
  const { getAuthToken } = useAuth();
  return useQuery<SupportInboxResponse>({
    queryKey: [SUPPORT_INBOX_KEY, companyId],
    queryFn: async () => {
      const token = await getAuthToken();
      return authedFetch<SupportInboxResponse>(
        `/api/companies/${encodeURIComponent(companyId)}/support-inbox`,
        token,
      );
    },
    refetchInterval: LIST_REFETCH_INTERVAL_MS,
    staleTime: 10_000,
  });
}

/** Full bodies for one thread — fetched only when a thread is selected. */
export function useSupportInboxThread(
  companyId: string,
  threadId: string | null,
) {
  const { getAuthToken } = useAuth();
  return useQuery<SupportInboxThreadResponse>({
    queryKey: [SUPPORT_INBOX_KEY, companyId, "thread", threadId],
    enabled: !!threadId,
    queryFn: async () => {
      const token = await getAuthToken();
      return authedFetch<SupportInboxThreadResponse>(
        `/api/companies/${encodeURIComponent(companyId)}/support-inbox/thread/${encodeURIComponent(
          threadId as string,
        )}`,
        token,
      );
    },
    staleTime: 10_000,
  });
}

/**
 * Returns a callback that warms the react-query cache for a thread's full
 * bodies. Call it on hover/focus of a thread row so the reading pane is already
 * populated (or in-flight) by the time the user clicks — the Gmail round-trip
 * overlaps the user's pointer travel instead of blocking after the click.
 */
export function usePrefetchSupportInboxThread(companyId: string) {
  const { getAuthToken } = useAuth();
  const queryClient = useQueryClient();
  return useCallback(
    (threadId: string) => {
      if (!threadId) return;
      void queryClient.prefetchQuery({
        queryKey: [SUPPORT_INBOX_KEY, companyId, "thread", threadId],
        queryFn: async () => {
          const token = await getAuthToken();
          return authedFetch<SupportInboxThreadResponse>(
            `/api/companies/${encodeURIComponent(companyId)}/support-inbox/thread/${encodeURIComponent(
              threadId,
            )}`,
            token,
          );
        },
        staleTime: 10_000,
      });
    },
    [companyId, getAuthToken, queryClient],
  );
}

const STATUS_RANK: Record<SupportMessageStatus, number> = {
  ticket_and_logged: 3,
  ticket: 2,
  logged: 1,
  new: 0,
};

/**
 * Group a flat message list into thread summaries, newest thread first. The
 * thread's rolled-up status is the most-informative of its messages, and its
 * ticket id is the first non-null found (for the "View ticket" affordance).
 */
export function useThreadSummaries(
  response: SupportInboxResponse | undefined,
): SupportInboxThreadSummary[] {
  return useMemo(() => {
    const messages = response?.messages ?? [];
    const byThread = new Map<string, SupportInboxThreadSummary>();

    for (const message of messages) {
      const existing = byThread.get(message.threadId);
      if (!existing) {
        byThread.set(message.threadId, {
          threadId: message.threadId,
          subject: message.subject,
          from: message.from,
          latestReceivedAt: message.receivedAt,
          messageCount: 1,
          status: message.badges.status,
          ticketId: message.badges.ticketId,
          messages: [message],
        });
        continue;
      }

      existing.messageCount += 1;
      existing.messages.push(message);
      if (message.receivedAt > existing.latestReceivedAt) {
        existing.latestReceivedAt = message.receivedAt;
        existing.from = message.from;
        existing.subject = message.subject;
      }
      if (STATUS_RANK[message.badges.status] > STATUS_RANK[existing.status]) {
        existing.status = message.badges.status;
      }
      if (!existing.ticketId && message.badges.ticketId) {
        existing.ticketId = message.badges.ticketId;
      }
    }

    return Array.from(byThread.values()).sort((a, b) =>
      b.latestReceivedAt.localeCompare(a.latestReceivedAt),
    );
  }, [response]);
}

export interface EnrollSupportGroupArgs {
  companyId: string;
  createGroup: boolean;
  groupEmail?: string;
  contactId?: string;
  slackChannelId?: string;
}

export interface EnrollSupportGroupResult {
  enrollment: { id: string; groupEmail: string; companyId: string };
}

/** Enroll (create/bind) a Google support group for a company. */
export function useEnrollSupportGroup() {
  const { getAuthToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation<EnrollSupportGroupResult, Error, EnrollSupportGroupArgs>({
    mutationFn: async (args) => {
      const token = await getAuthToken();
      const res = await fetch("/api/google-groups/enroll", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          companyId: args.companyId,
          createGroup: args.createGroup,
          groupEmail: args.groupEmail,
          contactId: args.contactId,
          slackChannelId: args.slackChannelId,
        }),
      });
      if (!res.ok) {
        let message = "Failed to enroll support group.";
        try {
          const body = (await res.json()) as { error?: string };
          if (body?.error) message = body.error;
        } catch {
          // keep default
        }
        throw new Error(message);
      }
      return (await res.json()) as EnrollSupportGroupResult;
    },
    onSuccess: (_result, args) => {
      void queryClient.invalidateQueries({
        queryKey: [SUPPORT_INBOX_KEY, args.companyId],
      });
    },
  });
}
